import { ofType } from 'redux-observable';
import {
  asyncScheduler,
  catchError,
  concatMap,
  EMPTY,
  forkJoin,
  from,
  map,
  Observable,
  scheduled,
  switchMap,
} from 'rxjs';
import {
  deleteTemplate,
  getTemplates,
  MacrosInput,
  saveTemplate,
  updateTemplate,
} from '@ax/service';
import { templatesSlice } from './index.ts';
import { Template } from './interfaces';
import { toast } from 'react-hot-toast';
import { fromMacrosServiceResponse, toMacrosServicePayload } from './utilities.ts';

export const onTemplateDiscardConfirmed = (action$: Observable<any>) =>
  action$.pipe(
    ofType(templatesSlice.actions.templatediscardConfirmed.type),
    concatMap((action) => {
      const { templatePostDiscardActionsExecuted } = templatesSlice.actions;
      return scheduled(
        [...action.payload.actions, templatePostDiscardActionsExecuted({})],
        asyncScheduler,
      );
    }),
  );

export const onLoadTemplateRequested = (action$: Observable<any>) =>
  action$.pipe(
    ofType(templatesSlice.actions.loadTemplatesRequested.type),
    switchMap((action) => {
      const rosTemplates$ = from(getTemplates(action.payload.providerId, 'ROS'));
      const peTemplates$ = from(getTemplates(action.payload.providerId, 'PE'));
      return forkJoin([rosTemplates$, peTemplates$]).pipe(
        map((sectionTemplatesPayload: MacrosInput[][]) => {
          const templatePayload = sectionTemplatesPayload.flat();
          const { loadTemplateListSucceeded } = templatesSlice.actions;
          const templateList: Template[] = templatePayload.map(fromMacrosServiceResponse);
          return loadTemplateListSucceeded({
            templates: templateList.filter((item) => item != null),
          });
        }),
        catchError(() => {
          return [
            templatesSlice.actions.loadTemplatesFailed({
              message: 'An error occurred while loading your templates',
            }),
          ];
        }),
      );
    }),
  );

export const onCreateTemplateRequested = (action$: Observable<any>) =>
  action$.pipe(
    ofType(templatesSlice.actions.createTemplateRequested.type),
    switchMap((action) => {
      return from(saveTemplate(toMacrosServicePayload(action.payload.template))).pipe(
        map((macros) => {
          const template = fromMacrosServiceResponse(macros);
          return templatesSlice.actions.createTemplateSucceeded({ template });
        }),
        catchError(() => {
          return [
            templatesSlice.actions.createTemplateFailed({
              message: 'An error occurred, please try again',
            }),
          ];
        }),
      );
    }),
  );

export const onUpdateTemplateRequested = (action$: Observable<any>) =>
  action$.pipe(
    ofType(templatesSlice.actions.updateTemplateRequested.type),
    switchMap((action) => {
      return from(updateTemplate(toMacrosServicePayload(action.payload.template))).pipe(
        map((macros) => {
          const template = fromMacrosServiceResponse(macros);
          return templatesSlice.actions.updateTemplateSucceeded({ template });
        }),
        catchError(() => {
          return [
            templatesSlice.actions.updateTemplateFailed({
              message: 'An error occurred, please try again',
            }),
          ];
        }),
      );
    }),
  );

export const onDeleteTemplateRequested = (action$: Observable<any>) =>
  action$.pipe(
    ofType(templatesSlice.actions.deleteTemplateRequested.type),
    switchMap((action) => {
      return from(deleteTemplate({ macroId: action.payload.templateId })).pipe(
        map((macros) => {
          return templatesSlice.actions.deleteTemplateSucceeded({ templateId: macros.id });
        }),
        catchError(() => {
          return [
            templatesSlice.actions.deleteTemplateFailed({
              message: 'An error occurred, please try again',
            }),
          ];
        }),
      );
    }),
  );

export const onLoadTemplatesFailed = (action$: Observable<any>) =>
  action$.pipe(
    ofType(templatesSlice.actions.loadTemplatesFailed.type),
    concatMap((action) => {
      toast.error(action.payload.message);
      return EMPTY;
    }),
  );

export const onCreateTemplateFailed = (action$: Observable<any>) =>
  action$.pipe(
    ofType(templatesSlice.actions.createTemplateFailed.type),
    concatMap((action) => {
      toast.error(action.payload.message);
      return EMPTY;
    }),
  );

export const onUpdateTemplateFailed = (action$: Observable<any>) =>
  action$.pipe(
    ofType(templatesSlice.actions.updateTemplateFailed.type),
    concatMap((action) => {
      toast.error(action.payload.message);
      return EMPTY;
    }),
  );

export const onDeleteTemplateFailed = (action$: Observable<any>) =>
  action$.pipe(
    ofType(templatesSlice.actions.deleteTemplateFailed.type),
    concatMap((action) => {
      toast.error(action.payload.message);
      return EMPTY;
    }),
  );

export const onCreatedTemplateSucceeded = (action$: Observable<any>) =>
  action$.pipe(
    ofType(templatesSlice.actions.createTemplateSucceeded.type),
    concatMap(() => {
      toast.success('Template created successfully');
      return EMPTY;
    }),
  );

export const onUpdateTemplateSucceeded = (action$: Observable<any>) =>
  action$.pipe(
    ofType(templatesSlice.actions.updateTemplateSucceeded.type),
    concatMap(() => {
      toast.success('Template updated successfully');
      return EMPTY;
    }),
  );

export const onDeleteTemplateSucceeded = (action$: Observable<any>) =>
  action$.pipe(
    ofType(templatesSlice.actions.deleteTemplateSucceeded.type),
    concatMap(() => {
      toast.success('Template deleted successfully');
      return EMPTY;
    }),
  );

export default [
  onLoadTemplateRequested,
  onCreateTemplateRequested,
  onUpdateTemplateRequested,
  onDeleteTemplateRequested,
  onTemplateDiscardConfirmed,
  onLoadTemplatesFailed,
  onCreateTemplateFailed,
  onUpdateTemplateFailed,
  onDeleteTemplateFailed,
  onCreatedTemplateSucceeded,
  onUpdateTemplateSucceeded,
  onDeleteTemplateSucceeded,
];
